import axios from 'axios';

let categoriesCache = null;
let townsCache = null;

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }
  delete api.defaults.headers.common.Authorization;
};

// Auth: login with identifier (email/phone) + password
export const loginUser = async (payload) => {
  try {
    const response = await api.post('/auth/login', payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Login failed');
  }
};

export const sendPhoneVerificationOtp = async () => {
  try {
    const response = await api.post('/auth/send-otp');
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Unable to send verification code');
  }
};

export const verifyPhoneVerificationOtp = async (otp) => {
  try {
    const response = await api.post('/auth/verify-otp', { otp });
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Unable to verify phone number');
  }
};

// Auth: signup
export const signupUser = async (payload) => {
  try {
    const response = await api.post('/auth/register', payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Sign up failed');
  }
};

export const requestPasswordReset = async (phone) => {
  try {
    const response = await api.post('/auth/forgot-password', { phone });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Unable to send password reset code');
  }
};

export const verifyPasswordResetOtp = async (phone, otp) => {
  try {
    const response = await api.post('/auth/forgot-password/verify-otp', { phone, otp });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Unable to verify password reset code');
  }
};

export const resetPassword = async (resetToken, newPassword, confirmPassword) => {
  try {
    const response = await api.post('/auth/forgot-password/reset', { resetToken, newPassword, confirmPassword });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Unable to reset password');
  }
};

export const changePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
  const payload = { currentPassword, newPassword, confirmPassword };
  const endpoints = ['/auth/change-password', '/auth/update-password', '/auth/password'];

  let lastError = null;

  for (const endpoint of endpoints) {
    try {
      const response = await api.post(endpoint, payload);
      return response.data?.data || response.data;
    } catch (error) {
      lastError = error;
      const status = error.response?.status;
      if (status !== 404 && status !== 405 && status !== 400) {
        throw new Error(error.response?.data?.message || 'Failed to change password');
      }
    }
  }

  throw new Error(lastError?.response?.data?.message || 'Failed to change password');
};

// Fetch all towns
export const fetchTowns = async () => {
  if (townsCache) return townsCache;
  try {
    const response = await api.get('/towns/');
    townsCache = response.data;
    return townsCache;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch towns');
  }
};

// Fetch all categories
export const fetchCategories = async () => {
  if (categoriesCache) return categoriesCache;
  try {
    const response = await api.get('/categories/');
    categoriesCache = response.data;
    return categoriesCache;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch categories');
  }
};

// Fetch all jobs
export const fetchJobs = async () => {
  try {
    const response = await api.get('/jobs/');
    return response.data.data; // Return the nested data array
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch jobs');
  }
};

// Fetch job by ID
export const fetchJobById = async (id) => {
  try {
    const response = await api.get(`/jobs/${id}`);
    return response.data; // Assume single job object
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch job');
  }
};

// Fetch personalized jobs feed
export const fetchMyFeed = async (filter = null) => {
  try {
    const url = filter ? `/jobs/my-feed?filter=${filter}` : '/jobs/my-feed';
    const response = await api.get(url);
    return response.data.data; // Return the nested data array
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch personalized feed');
  }
};

// Fetch saved jobs
export const fetchSavedJobs = async () => {
  try {
    const response = await api.get('/jobs/saved');
    return response.data.data; // Return saved items array
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch saved jobs');
  }
};

// Fetch active jobs
export const fetchActiveJobs = async () => {
  try {
    const response = await api.get('/jobs/active');
    return response.data.data; // Return active jobs
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch active jobs');
  }
};

// Fetch expired jobs
export const fetchExpiredJobs = async () => {
  try {
    const response = await api.get('/jobs/expired');
    return response.data?.data || response.data || [];
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch expired jobs');
  }
};

const formatFutureDateForApi = (value) => {
  if (!value) return '';

  if (value.includes('.')) {
    return value;
  }

  const [year, month, day] = String(value).split('-');
  if (!year || !month || !day) {
    return value;
  }

  return `${day}.${month}.${year}`;
};

export const repostJob = async (jobId, expiresAt) => {
  try {
    const formattedDate = formatFutureDateForApi(expiresAt);
    const response = await api.post(`/jobs/${encodeURIComponent(jobId)}/repost`, { expiresAt: formattedDate });
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to repost job');
  }
};

// Fetch jobs together with their hired workers
export const fetchHiredApplicants = async () => {
  try {
    const response = await api.get('/jobs/hired-applicants');
    return response.data?.data || response.data || [];
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch hired jobs');
  }
};

// Mark a hired application as completed
export const completeHiredApplication = async (applicationId) => {
  try {
    const response = await api.post(`/jobs/applications/${encodeURIComponent(applicationId)}/complete`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.error || error.response?.data?.data?.message || 'Unable to complete this worker\'s work.');
  }
};

// Complete all hired workers for a job as the employer
export const completeJobAsEmployer = async (jobId) => {
  try {
    const response = await api.post(`/jobs/${encodeURIComponent(jobId)}/complete-as-employer`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.error || error.response?.data?.data?.message || 'Unable to complete the entire job.');
  }
};

// Rate a worker after completing a job
export const rateJob = async (jobId, payload) => {
  try {
    const response = await api.post(`/jobs/${encodeURIComponent(jobId)}/rate`, payload);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to submit rating');
  }
};

// Update a client job
export const updateJob = async (jobId, jobData) => {
  try {
    const response = await api.put(`/jobs/${encodeURIComponent(jobId)}`, jobData);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update job');
  }
};

// Close a client job
export const closeJob = async (jobId) => {
  try {
    const response = await api.post(`/jobs/${encodeURIComponent(jobId)}/close`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to close job');
  }
};

// Fetch skills for category slug
export const fetchSkillsByCategorySlug = async (slug) => {
  try {
    const response = await api.get(`/skills/category/${slug}`);
    return response.data.data; // Return skills list
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch skills');
  }
};

// Add a skill to the worker's profile using the user-skill API
export const addUserSkill = async (payload) => {
  try {
    const response = await api.post('/userskills/skills/', payload);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to add skill');
  }
};

// Remove a worker skill from the current profile
export const removeUserSkill = async (skillId) => {
  try {
    const response = await api.delete(`/userskills/skills/${encodeURIComponent(skillId)}`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to remove skill');
  }
};

// Fetch client applications grouped by job
export const fetchClientApplications = async () => {
  try {
    const response = await api.get('/jobs/applications?view=list');
    return response.data?.data || response.data || [];
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch applications');
  }
};

// Fetch a worker's complete profile for client application review
export const fetchUserProfile = async (userId) => {
  try {
    const response = await api.get(`/profile/${encodeURIComponent(userId)}`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user profile');
  }
};

// Generate and fetch a worker's CV
export const fetchUserCv = async (userId) => {
  try {
    const response = await api.get(`/cv/${encodeURIComponent(userId)}`);
    const payload = response.data?.data || response.data;
    return payload?.data || payload;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch CV');
  }
};

// Update an application using its application ID
export const shortlistApplicant = async (applicationId) => {
  try {
    const response = await api.post(`/jobs/applications/${encodeURIComponent(applicationId)}/shortlist`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to shortlist applicant');
  }
};

export const rejectApplicant = async (applicationId) => {
  try {
    const response = await api.post(`/jobs/applications/${encodeURIComponent(applicationId)}/reject`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to reject applicant');
  }
};

export const hireApplicant = async (applicationId) => {
  try {
    const response = await api.post(`/jobs/applications/${encodeURIComponent(applicationId)}/hire`);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to hire applicant');
  }
};

// Fetch jobs applied to by the current worker
export const fetchAppliedJobs = async () => {
  try {
    const response = await api.get('/jobs/applied');
    return response.data.data; // Return applied jobs list view
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch applied jobs');
  }
};

export const cancelApplication = async (applicationId) => {
  try {
    const response = await api.post(`/jobs/applications/${encodeURIComponent(applicationId)}/cancel`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to cancel application');
  }
};

export const cancelHire = async (applicationId) => {
  try {
    const response = await api.post(`/jobs/applications/${encodeURIComponent(applicationId)}/cancel-hire`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to cancel hire');
  }
};

export const fetchMyHiredJobs = async () => {
  try {
    const response = await api.get('/jobs/my-hired');
    return response.data?.data || response.data || [];
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch hired jobs');
  }
};

export const fetchMyCompletedJobs = async () => {
  try {
    const response = await api.get('/jobs/my-completed');
    return response.data?.data || response.data || [];
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch completed jobs');
  }
};

export const fetchClientCompletedJobs = async () => {
  try {
    const response = await api.get('/jobs/completed');
    return response.data?.data || response.data || [];
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch completed jobs');
  }
};

// Save a job
export const saveJob = async (jobId) => {
  try {
    const response = await api.post('/jobs/save', { jobId });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to save job');
  }
};

// Remove a job from the current worker's saved jobs
export const unsaveJob = async (jobId) => {
  try {
    const response = await api.post('/jobs/unsave', { jobId });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to unsave job');
  }
};

// Apply to a job
export const applyJob = async (jobId) => {
  try {
    const response = await api.post('/jobs/apply', { jobId });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to apply to job');
  }
};

// Create a new job
export const createJob = async (jobData) => {
  try {
    const response = await api.post('/jobs/', jobData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create job');
  }
};

// Fetch profile completion status
export const fetchProfileStatus = async () => {
  try {
    const response = await api.get('/profile/getprofilestatus');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch profile status');
  }
};

export const fetchDashboardData = async () => {
  try {
    const response = await api.get('/profile/dashboard');
    return response.data?.data || response.data || {};
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch dashboard data');
  }
};

// Update the current user's profile with only the edited fields
export const updateProfile = async (payload) => {
  try {
    const response = await api.put('/profile', payload);
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update profile');
  }
};

// Upload or replace the current user's profile photo
export const uploadProfilePhoto = async (file) => {
  try {
    const formData = new FormData();
    formData.append('profilePhoto', file);
    // Let the browser set multipart/form-data with its boundary.
    const response = await api.post('/profile/photo', formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data?.data || response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to upload profile photo');
  }
};

export default api;
